'use client';

import { motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { Award, BadgeCheck, Gem, Handshake, Shield, Sparkles, TrendingUp } from 'lucide-react';
import { listItemVariants, staggerContainerVariants } from '@/lib/animations';
import type { Service } from '@/services/ProductMiceDataService';

export interface ProductMiceWhyUsProps {
    items: Service[];
}

const ICONS: LucideIcon[] = [Award, Shield, Sparkles, Gem, TrendingUp, BadgeCheck, Handshake];

/**
 * MICE detay: Neden Biz — services_repeater; kurumsal kart ızgarası, ikon sırası otomatik.
 */
export default function ProductMiceWhyUs({ items }: ProductMiceWhyUsProps) {
    const filtered = items.filter((row) => {
        if (!row || typeof row !== 'object') return false;
        const title = row.title || row.name || row.value || row.text || '';
        return typeof title === 'string' && title.trim().length > 0;
    });

    if (filtered.length === 0) return null;

    return (
        <motion.ul
            className="m-0 grid list-none grid-cols-1 gap-5 p-0 md:grid-cols-2 md:gap-6 lg:gap-7"
            role="list"
            variants={staggerContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '0px 0px -80px 0px' }}
        >
            {filtered.map((row, index) => {
                const title = String(row.title || row.name || row.value || row.text || '').trim();
                const description = String(row.description || row.desc || row.detail || '').trim();
                const Icon = ICONS[index % ICONS.length];

                return (
                    <motion.li key={`${title}-${index}`} className="group h-full" variants={listItemVariants}>
                        <article className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200/85 bg-gradient-to-br from-white via-white to-gray-50/90 p-6 shadow-[0_2px_12px_rgba(15,23,42,0.04)] transition-all duration-300 before:pointer-events-none before:absolute before:inset-y-3 before:left-0 before:w-1 before:rounded-full before:bg-gradient-to-b before:from-alsero-first before:to-alsero-first/40 before:opacity-90 before:content-[''] hover:border-alsero-first/20 hover:shadow-[0_8px_28px_rgba(15,23,42,0.08)] md:p-7">
                            <div className="flex flex-1 flex-col gap-4 pl-3">
                                <div className="flex items-start gap-4">
                                    <div
                                        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-alsero-first/[0.09] text-alsero-first ring-1 ring-alsero-first/10 transition-transform duration-300 group-hover:scale-[1.03]"
                                        aria-hidden
                                    >
                                        <Icon className="h-6 w-6" strokeWidth={1.65} />
                                    </div>
                                    <div className="min-w-0 flex-1 space-y-2">
                                        <h3 className="text-lg font-semibold leading-snug tracking-tight text-alsero-dark-gray md:text-xl">
                                            {title}
                                        </h3>
                                        {description ? (
                                            <p className="text-sm leading-relaxed text-alsero-text-tour md:text-[0.9375rem]">
                                                {description}
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </article>
                    </motion.li>
                );
            })}
        </motion.ul>
    );
}
